// Severity-Trio — bento-spec-sprint6.md.
//
// Cyan = OK / Monitoring (Normal-Zustand)
// Amber = Warn (Schwellwert nahe, Aufmerksamkeit)
// Pink  = Critical (Sofort-Action: <14T Frist, Cap-Bruch, etc.)
//
// Orthogonal zu Firmen-Farben (firma-colors.ts) — niemals mischen:
// - Severity-Tiles (Hero/KPI/Decision-Queue/Tax/Mahnungen) bekommen NUR Severity.
// - Firmen-Tiles (Per-Company-Cards) bekommen NUR Firmen-Border, KEINE Severity.

export type Severity = "ok" | "warn" | "critical";

// Backend-Aliase (existing): KpiStatus("ok"/"warn"/"crit"/"no_data"),
// AnomalySeverity("info"/"warn"/"critical"), Steuer-/Termin-Severity ditto.
// Wir mappen sie auf das Trio.
export type SeverityInput =
  | Severity
  | "info"
  | "monitor"
  | "crit"
  | "no_data"
  | null
  | undefined;

export function normalizeSeverity(input: SeverityInput): Severity {
  if (!input) return "ok";
  switch (input) {
    case "critical":
    case "crit":
      return "critical";
    case "warn":
      return "warn";
    case "ok":
    case "info":
    case "monitor":
    case "no_data":
      return "ok";
  }
}

export interface SeverityStyle {
  /** 1px Border-Klasse. */
  border: string;
  /** Sehr subtler Tile-Background. */
  bg: string;
  /** Staerker, fuer Pills/Badges. */
  bgStrong: string;
  /** Text/Fg-Tint fuer Severity-Labels. */
  text: string;
  /** Dot fuer Mini-Indikator. */
  dot: string;
  /** Inner-Glow nur fuer Pink (Spec: "Innen-Glow bei Severity-pink"). */
  glow: string;
  /** Pulse-Hint fuer Pink — Spec: "subtle pulse 1.5s" (Push 3). */
  pulse: string;
}

export const SEVERITY_STYLES: Record<Severity, SeverityStyle> = {
  ok: {
    border: "border-cyan-500/30",
    bg: "bg-cyan-500/[0.04]",
    bgStrong: "bg-cyan-500/15",
    text: "text-cyan-200",
    dot: "bg-cyan-400",
    glow: "",
    pulse: "",
  },
  warn: {
    border: "border-amber-500/40",
    bg: "bg-amber-500/[0.05]",
    bgStrong: "bg-amber-500/15",
    text: "text-amber-200",
    dot: "bg-amber-400",
    glow: "",
    pulse: "",
  },
  critical: {
    border: "border-pink-500/50",
    bg: "bg-pink-500/[0.06]",
    bgStrong: "bg-pink-500/15",
    text: "text-pink-200",
    dot: "bg-pink-400",
    glow: "shadow-[inset_0_0_24px_-8px_rgba(236,72,153,0.45)]",
    pulse: "animate-pulse",
  },
};

export function severityToTailwind(input: SeverityInput): SeverityStyle {
  return SEVERITY_STYLES[normalizeSeverity(input)];
}

/** Hoechste Severity in einer Liste — fuer Aggregat-Tiles wie Decision-Queue. */
export function severityMax(items: Iterable<SeverityInput>): Severity {
  let max: Severity = "ok";
  for (const it of items) {
    const norm = normalizeSeverity(it);
    if (norm === "critical") return "critical";
    if (norm === "warn") max = "warn";
  }
  return max;
}
